/**
 * IL TEMPO REALE DI LIMULO — lo stream SSE del BFF, che per la TMA porta UN solo evento:
 * `Tournament.action.changed` («le tue azioni potrebbero essere cambiate, rileggile»).
 * Dietro `VITE_FEATURE_LIMULO_REALTIME_ENABLED`, acceso solo in `.env.staging` (tma-orcb 08:20Z, P3).
 *
 * Il trasporto, misurato su `qlash-limulo@7e0eae2` (dispatch tma-orcb-7-azioni, punto 1):
 *  · `GET /qlash-api/v1/realtime/stream` (`router.js:1271-1276`), SSE. Il client NON sceglie i topic:
 *    `openStream` rifiuta quelli del client (403) e apre sempre `user:<uid>`, `notifications:<uid>`,
 *    `organization:<org>` (`app-qlash-services/backend/features/realtime.js:42-46, 184-198`);
 *  · l'auth e' SOLO negli header: `Authorization` (`middlewares/auth.ts:219`) e `X-AM-ORGANIZATION`
 *    (`:247`). Nessun token in query ⇒ un `EventSource` nativo non puo' aprirlo, e qui si legge con
 *    `fetch` e un parser SSE nostro (niente dipendenze nuove, P1);
 *  · i frame (`express-stream.ts:18-19, 33-39`): `retry: 5000`, poi `event: <nome>\ndata: <json>`.
 *    `ready` all'apertura, `heartbeat` ogni 25 s, `realtime` per i domini: il `data` e' la busta
 *    intera `{eventId, type, organizationId, topics, timestamp, payload}`, e per
 *    `type === 'Tournament.action.changed'` il payload e' `{tournamentId, matchId|null, reason|null}`
 *    (`domain-events.ts:94-105`);
 *  · consegna best-effort, **senza replay** (`docs/REALTIME-BROKER.md:33-41`) ⇒ a ogni (ri)apertura,
 *    dopo `ready`, si rilegge: un evento perso mentre lo stream era giu' si recupera cosi'.
 *
 * ⛔ Pura e senza `import.meta.env`, `window`, `document`, orologio o timer: `fetch`, il token, l'identita'
 * della sessione e l'attesa li passa chi la monta (`hooks/useTempoRealeLimulo.ts`), e i banchi li fingono.
 */
import type { LimuloSession } from './auth.ts';

export const LIMULO_REALTIME_PATH = '/v1/realtime/stream';
export const EVENTO_AZIONI_CAMBIATE = 'Tournament.action.changed';

/** Il `retry:` del server se non ne arriva uno (lo stesso valore che manda: `express-stream.ts:29`). */
export const RITARDO_BASE_MS = 5_000;
/** Il tetto del backoff: oltre, un'app aperta tenterebbe di rado senza guadagnare niente. */
export const RITARDO_MASSIMO_MS = 60_000;
/** Il minimo per il `retry:` del server (revisione di #423): un `retry: 1` non deve diventare un ciclo stretto. */
export const RITARDO_MINIMO_MS = 1_000;
/**
 * 🔴 **Il cane da guardia** (revisione di #423, punto a): senza NESSUN frame per tanto, lo stream si da'
 * per morto, si chiude e si riapre. Il `heartbeat` arriva ogni 25 s (`broker.ts:237-243`), quindi 60 s sono
 * piu' di due battiti persi. Il caso: un telefono che cambia rete (o un NAT che scade) lascia `read()`
 * appeso SENZA errore, e lo stream sembrerebbe vivo per sempre. ⚠️ Una SCELTA, da misurare nelle WebView.
 */
export const SILENZIO_MASSIMO_MS = 60_000;

export interface FrameSse {
  /** `message` se il frame non lo dice (la regola dello standard SSE). */
  event: string;
  data: string;
  retry?: number;
}

/**
 * Il parser SSE, incrementale: si nutre di pezzi di testo come arrivano (un frame puo' stare a cavallo
 * di due pezzi, e una riga anche) e rende i frame completi. Righe `:` = commenti; piu' righe `data:`
 * si uniscono con `\n`; `\r\n` e `\r` valgono come `\n`.
 */
export function creaLettoreSse(): { nutri: (pezzo: string) => FrameSse[] } {
  let resto = '';
  // Un pezzo finito con `\r` ha gia' chiuso la riga: se il pezzo dopo comincia con `\n`, quel `\n` e' la
  // meta' dello stesso `\r\n`, non una riga vuota (che chiuderebbe il frame a meta': revisione di #423).
  let saltaLf = false;
  let event = '';
  let data: string[] = [];
  let retry: number | undefined;
  const chiudiFrame = (out: FrameSse[]) => {
    if (data.length > 0 || retry !== undefined) {
      out.push({ event: event || 'message', data: data.join('\n'), ...(retry === undefined ? {} : { retry }) });
    }
    event = '';
    data = [];
    retry = undefined;
  };
  return {
    nutri(pezzo) {
      const out: FrameSse[] = [];
      // Un pezzo vuoto (il decoder che trattiene un carattere a piu' byte) non cambia niente, nemmeno il
      // `\r` in sospeso. ⚠️ E il test viene PRIMA del salto (revisione di #425): se il `\n` saltato era il
      // pezzo intero, `saltaLf` si spegne qui sotto — con la guardia dopo, restava acceso e mangiava il
      // `\n` del pezzo seguente, fondendo due frame.
      if (pezzo === '') return out;
      if (saltaLf && pezzo.startsWith('\n')) pezzo = pezzo.slice(1);
      saltaLf = pezzo.endsWith('\r');
      resto += pezzo;
      const righe = resto.split(/\r\n|\r|\n/);
      // L'ultima riga e' incompleta finche' non arriva il suo a-capo.
      resto = righe.pop() ?? '';
      for (const riga of righe) {
        if (riga === '') {
          chiudiFrame(out);
          continue;
        }
        if (riga.startsWith(':')) continue;
        const i = riga.indexOf(':');
        const campo = i === -1 ? riga : riga.slice(0, i);
        const valore = i === -1 ? '' : riga.slice(i + 1).replace(/^ /, '');
        if (campo === 'event') event = valore;
        else if (campo === 'data') data.push(valore);
        else if (campo === 'retry' && /^\d+$/.test(valore)) retry = Number(valore);
      }
      return out;
    },
  };
}

export type FrameLetto =
  | { kind: 'ready' }
  | { kind: 'heartbeat' }
  | { kind: 'azioni'; tournamentId: string; matchId: string | null; reason: string | null }
  | { kind: 'altro' };

/** Cosa dice un frame alla TMA. Tutto cio' che non e' `ready`, `heartbeat` o il campanello si ignora. */
export function frameLettoOf(frame: FrameSse): FrameLetto {
  if (frame.event === 'ready') return { kind: 'ready' };
  if (frame.event === 'heartbeat') return { kind: 'heartbeat' };
  if (frame.event !== 'realtime') return { kind: 'altro' };
  let busta: unknown;
  try {
    busta = JSON.parse(frame.data);
  } catch {
    return { kind: 'altro' };
  }
  const b = busta !== null && typeof busta === 'object' ? (busta as Record<string, unknown>) : {};
  if (b.type !== EVENTO_AZIONI_CAMBIATE) return { kind: 'altro' };
  const p = b.payload !== null && typeof b.payload === 'object' ? (b.payload as Record<string, unknown>) : {};
  const tournamentId = typeof p.tournamentId === 'string' ? p.tournamentId : '';
  if (tournamentId === '') return { kind: 'altro' };
  const matchId = typeof p.matchId === 'string' && p.matchId !== '' ? p.matchId : null;
  const reason = typeof p.reason === 'string' && p.reason !== '' ? p.reason : null;
  return { kind: 'azioni', tournamentId, matchId, reason };
}

export type TagDaRileggere =
  | 'LimuloAzioni'
  | { type: 'LimuloMatch'; id: string }
  | { type: 'LimuloTournament'; id: string };

/** Il `reason` del motore quando un torneo cambia stato (`core/transizioni.js:931`, a TUTTE le parti). */
export const MOTIVO_STATO_TORNEO = 'tournament-state';

/**
 * Cosa rileggere per un campanello (tma-orcb 08:20Z, P2): le azioni, e l'incontro nominato — e' la
 * scheda d'incontro aperta a restare su «Aspettiamo l'avversario…» (lettura dal vivo del 26/09, FASE 3
 * eFootball: ~15 s, il ritmo delle riletture). Nient'altro.
 */
export function tagDaRileggereOf(evento: {
  tournamentId?: string;
  matchId: string | null;
  reason?: string | null;
}): TagDaRileggere[] {
  const tag: TagDaRileggere[] =
    evento.matchId === null ? ['LimuloAzioni'] : ['LimuloAzioni', { type: 'LimuloMatch', id: evento.matchId }];
  // 🔴 [modale dell'esito, tma-orcr-22-esito] Un torneo che cambia stato rilegge anche la SUA scheda: e'
  // l'unico segnale per chi e' gia' uscito dal tabellone (niente piu' azioni li') che il torneo e' finito,
  // e quindi che il suo piazzamento esiste. Rilegge solo chi ha quella scheda aperta o in attesa
  // (`OsservatoreEsiti`): nessuna lettura nuova se nessuno la guarda.
  if (evento.reason === MOTIVO_STATO_TORNEO && evento.tournamentId) {
    tag.push({ type: 'LimuloTournament', id: evento.tournamentId });
  }
  return tag;
}

/** Il `retry:` del server come lo usa il ciclo: `0` = il nostro, sotto il minimo = il minimo. */
export function retryDelServerOf(valore: number): number {
  return valore <= 0 ? RITARDO_BASE_MS : Math.max(valore, RITARDO_MINIMO_MS);
}

/** Dopo `n` fallimenti di fila: il `retry` del server, poi il doppio a ogni giro, fino al tetto. */
export function ritardoDopo(fallimenti: number, retryServer: number = RITARDO_BASE_MS): number {
  const base = retryServer > 0 ? retryServer : RITARDO_BASE_MS;
  return Math.min(base * 2 ** Math.max(0, fallimenti - 1), RITARDO_MASSIMO_MS);
}

/**
 * La risposta all'apertura → cosa fare.
 *  · 401: il token non vale. UNA nuova richiesta a `getLimuloToken()` e un nuovo tentativo, poi basta (P1).
 *    ⛔ Senza `endLimuloSession()`: lo stream NON possiede la sessione, che e' condivisa da tutte le letture
 *    Limulo — buttarla da qui farebbe rifare l'exchange all'intera sezione per un rifiuto dello stream
 *    (tma-orcb 08:42Z). Se il token riletto e' lo stesso, il secondo 401 chiude e basta;
 *  · 403 e 424 (e gli altri 4xx): l'interruttore `enableRealtime` spento, un provider assente, una
 *    richiesta che il server non accetta — riprovare darebbe lo stesso rifiuto: si smette;
 *  · 5xx e 429: puo' cambiare da solo, si riprova col backoff.
 */
export type EsitoApertura = 'aperto' | 'rinnova' | 'smetti' | 'riprova';
export function esitoAperturaOf(status: number): EsitoApertura {
  if (status >= 200 && status < 300) return 'aperto';
  if (status === 401) return 'rinnova';
  if (status === 429 || status >= 500) return 'riprova';
  return 'smetti';
}

/**
 * L'identita' con cui lo stream e' stato aperto: l'utente Logto a cui la sessione e' legata, l'org e
 * la sessione mobile del BFF (`qlashSessionId`, dentro il JWT interno: firmato a `mobile-auth.js:808`,
 * letto a `:1013-1014`). Un rinnovo del token la lascia uguale (il refresh cambia `jwtId` e l'hash del
 * refresh, non `sessionId`: `:948-949`); un logout, un altro account o una sessione nuova la cambiano.
 * `null` = nessuna sessione.
 */
export function identitaDellaSessione(sessione: LimuloSession | null): string | null {
  if (sessione === null) return null;
  let sid = '';
  try {
    const corpo = sessione.token.split('.')[1] ?? '';
    const json = atob(corpo.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(corpo.length / 4) * 4, '='));
    const claim = (JSON.parse(json) as Record<string, unknown>).qlashSessionId;
    sid = typeof claim === 'string' ? claim : '';
  } catch {
    sid = '';
  }
  return `${sessione.boundTo}|${sessione.organizationId}|${sid}`;
}

export type FineFlusso = 'chiuso' | 'senza-sessione' | 'rifiutato' | 'spento';

export interface DipendenzeFlusso {
  fetch: (url: string, init: RequestInit) => Promise<Response>;
  /** L'URL intero dello stream (`<limuloApiUrl>/v1/realtime/stream`). */
  url: string;
  token: () => Promise<string | null>;
  /**
   * C'e' una sessione a riposo (`peekLimuloSession() !== null`)? Serve a leggere un token `null`:
   * `getLimuloToken` lo rende anche per un rinnovo fallito per la rete, che lascia la sessione in piedi.
   */
  haSessione: () => boolean;
  /** `X-AM-ORGANIZATION`: l'host la legge da li' (`middlewares/auth.ts:247`). Vuoto = non si manda. */
  organizzazione: string;
  /** `identitaDellaSessione(peekLimuloSession())`, riletta a ogni frame. */
  identita: () => string | null;
  /** Un'attesa che si interrompe col segnale (rende comunque: chi chiama guarda `signal.aborted`). */
  attendi: (ms: number, signal: AbortSignal) => Promise<void>;
  /** Dopo `ready`: lo stream e' vivo, e cio' che e' successo prima va riletto. */
  suAperto: () => void;
  suAzioni: (evento: { tournamentId: string; matchId: string | null; reason: string | null }) => void;
}

/**
 * Tiene aperto lo stream finche' `signal` non lo chiude, riaprendolo quando cade. Rende perche' ha smesso.
 *
 * ⚠️ L'identita' si ricontrolla a OGNI frame (almeno il `heartbeat`, ogni 25 s): se non e' piu' quella
 * dell'apertura (tma-orcb 08:20Z: «un flusso aperto con l'identita' vecchia non deve restare vivo»), lo
 * stream si chiude e si riapre subito col token nuovo — o non si riapre, se una sessione non c'e' piu'.
 * Il logout vero lo chiude prima, e da fuori: `bridgeAuthenticated` cade e il segnale scatta.
 */
export async function tieniApertoIlFlusso(deps: DipendenzeFlusso, signal: AbortSignal): Promise<FineFlusso> {
  let fallimenti = 0;
  let retryServer = RITARDO_BASE_MS;
  let rinnovato = false;
  const pausa = async () => {
    fallimenti += 1;
    await deps.attendi(ritardoDopo(fallimenti, retryServer), signal);
  };
  while (!signal.aborted) {
    const token = await deps.token();
    // ⚠️ Portante (revisione di #423): col doppio montaggio di StrictMode, o con l'app nascosta mentre il
    // token era in volo, il segnale e' gia' chiuso qui — e senza questa riga si aprirebbe uno stream che
    // nessuno chiude piu' (un `addEventListener('abort')` su un segnale gia' chiuso non scatta mai).
    if (signal.aborted) break;
    if (token === null) {
      // Nessuna sessione ⇒ niente da aprire. Una sessione c'e' ma il token no (un rinnovo caduto per la
      // rete) ⇒ si riprova col backoff, invece di spegnere il tempo reale fino al prossimo rientro.
      // ⚠️ Residuo dichiarato (revisione di #425): senza sessione a riposo `getLimuloToken` tenta l'exchange,
      // e se l'exchange cade per la rete (anche il timeout di 15 s all'avvio) qui si esce. Il tempo reale
      // resta spento fino al prossimo rientro dell'app, anche se nel frattempo la sessione la creano le
      // letture RTK. Le letture restano al loro ritmo: si perde l'anticipo, non i dati.
      if (!deps.haSessione()) return 'senza-sessione';
      await pausa();
      continue;
    }
    const allApertura = deps.identita();
    const questo = new AbortController();
    const inoltra = () => questo.abort();
    signal.addEventListener('abort', inoltra, { once: true });
    try {
      const headers: Record<string, string> = { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' };
      if (deps.organizzazione) headers['X-AM-ORGANIZATION'] = deps.organizzazione;
      let risposta: Response;
      // Il cane da guardia anche sull'APERTURA (revisione di #425): una fetch appesa prima degli header
      // resterebbe appesa fino al prossimo rientro. Scaduto il silenzio massimo si chiude, e si riprova.
      const guardiaApertura = new AbortController();
      void deps.attendi(SILENZIO_MASSIMO_MS, guardiaApertura.signal).then(() => {
        if (!guardiaApertura.signal.aborted) questo.abort();
      });
      try {
        risposta = await deps.fetch(deps.url, { method: 'GET', headers, cache: 'no-store', signal: questo.signal });
      } catch {
        // Subito, non nel `finally`: il `finally` gira DOPO la `pausa()` qui sotto, e per tutto il backoff
        // resterebbe vivo anche il timer da 60 s della guardia (revisione di #427, A5).
        guardiaApertura.abort();
        if (signal.aborted) break;
        await pausa();
        continue;
      } finally {
        guardiaApertura.abort();
      }
      const esito = esitoAperturaOf(risposta.status);
      // Il corpo di una risposta che non leggeremo si chiude: altrimenti resta appeso alla connessione.
      if (esito !== 'aperto') void risposta.body?.cancel().catch(() => undefined);
      if (esito === 'smetti') return 'spento';
      if (esito === 'rinnova') {
        if (rinnovato) return 'rifiutato';
        rinnovato = true;
        await pausa();
        continue;
      }
      if (esito === 'riprova' || risposta.body === null) {
        await pausa();
        continue;
      }
      const lettore = creaLettoreSse();
      const decoder = new TextDecoder();
      const reader = risposta.body.getReader();
      let cambiata = false;
      try {
        for (;;) {
          // Il cane da guardia: la lettura corre contro il silenzio massimo. Chi perde la corsa si spegne
          // (il timer con la sua guardia, la lettura con `questo.abort()` nel `finally`).
          const guardia = new AbortController();
          const silenzio = deps.attendi(SILENZIO_MASSIMO_MS, guardia.signal).then(() => 'silenzio' as const);
          let letto: ReadableStreamReadResult<Uint8Array> | 'silenzio';
          try {
            letto = await Promise.race([reader.read(), silenzio]);
          } finally {
            guardia.abort();
          }
          if (letto === 'silenzio') break;
          const { value, done } = letto;
          if (done) break;
          // `stream: true`: un carattere di piu' byte puo' stare a cavallo di due pezzi.
          for (const frame of lettore.nutri(decoder.decode(value, { stream: true }))) {
            if (frame.retry !== undefined) retryServer = retryDelServerOf(frame.retry);
            if (deps.identita() !== allApertura) {
              cambiata = true;
              break;
            }
            const f = frameLettoOf(frame);
            if (f.kind === 'ready') {
              // Il token e' buono: un 401 di dopo ha di nuovo diritto al suo secondo tentativo.
              rinnovato = false;
              deps.suAperto();
            } else if (f.kind === 'heartbeat') {
              // ⚠️ Il backoff si azzera al PRIMO battito, non al `ready` (revisione di #423, punto c): un proxy
              // che chiude subito dopo `ready` riaprirebbe ogni 5 s per sempre, rileggendo ogni volta.
              fallimenti = 0;
            } else if (f.kind === 'azioni') {
              deps.suAzioni({ tournamentId: f.tournamentId, matchId: f.matchId, reason: f.reason });
            }
          }
          if (cambiata) break;
        }
      } catch {
        // Lo stream e' caduto (rete, o il nostro `abort`): sotto si decide se riaprire.
      } finally {
        questo.abort();
        try {
          reader.releaseLock();
        } catch {
          // Una lettura ancora pendente: il `abort` qui sopra la chiude comunque.
        }
      }
      if (signal.aborted) break;
      // Un'identita' nuova si riapre SUBITO col suo token; uno stream caduto aspetta il suo turno.
      if (!cambiata) await pausa();
    } finally {
      signal.removeEventListener('abort', inoltra);
    }
  }
  return 'chiuso';
}
